'use client';

import { type ChatFile, removeChatFile, toChatFile } from '@/lib/chat/chatFiles';
import { supabase } from '@/lib/supabase/client';
import { useCallback, useEffect, useRef, useState } from 'react';

interface UseRealtimeChatProps {
  roomName: string;
  username: string;
  userId: string;
}

export interface ChatMessage {
  id: number;
  content: string;
  userId: string;
  userName: string;
  sentAt: string;
  withNotification: boolean;
  file: ChatFile | null;
}

/** Payload built by internal.broadcast_message_to_realtime_trigger. */
type BroadcastMessagePayload = {
  id: number;
  content: string;
  userId: string;
  userName: string;
  sentAt: string;
  withNotification: boolean | null;
  filePath?: string | null;
  fileName?: string | null;
  fileType?: string | null;
  fileSize?: number | null;
};

const EVENT_MESSAGE_TYPE = 'message';
const EVENT_MESSAGE_DELETED_TYPE = 'message_deleted';

function fromBroadcastPayload(payload: BroadcastMessagePayload): ChatMessage {
  return {
    id: payload.id,
    content: payload.content,
    userId: payload.userId,
    userName: payload.userName,
    sentAt: payload.sentAt,
    withNotification: payload.withNotification ?? false,
    file: toChatFile(payload.filePath, payload.fileName, payload.fileType, payload.fileSize),
  };
}

export function useRealtimeChat({ roomName, username, userId }: UseRealtimeChatProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [deletedMessageIds, setDeletedMessageIds] = useState<Set<number>>(new Set());
  const [channel, setChannel] = useState<ReturnType<typeof supabase.channel> | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const topicRef = useRef(null);

  useEffect(() => {
    // const chatId = 'test'
    topicRef.current = `topic:${roomName}`;

    const newChannel = supabase
      .channel(topicRef.current, {
        config: { private: true },
      })
      .on('broadcast', { event: EVENT_MESSAGE_TYPE }, (payload) => {
        setMessages((current) => [...current, fromBroadcastPayload(payload.payload as BroadcastMessagePayload)]);
      })
      .on('broadcast', { event: EVENT_MESSAGE_DELETED_TYPE }, (payload) => {
        const { id } = payload.payload as { id: number };
        setMessages((current) => current.filter((message) => message.id !== id));
        setDeletedMessageIds((current) => new Set(current).add(id));
      })
      .subscribe(async (status, error) => {
        if (status === 'SUBSCRIBED') {
          setIsConnected(true);
          console.log(`Connected to '${roomName}'`);
        } else {
          setIsConnected(false);
        }

        if (error) {
          console.error(error);
        }
      });

    setChannel(newChannel);

    return () => {
      supabase.removeChannel(newChannel);
    };
  }, [roomName, username]);

  /**
   * @param file - An already uploaded chat file to attach; the caller owns the upload.
   * @returns The RPC error, if any, so the caller can clean up the upload.
   */
  const sendMessage = useCallback(
    async (content: string, sendNotification?: boolean, file?: ChatFile | null) => {
      if (!channel || !isConnected) return { error: new Error('Not connected to chat') };

      const { error } = await supabase.rpc("send_message", {
        content: content,
        topic: topicRef.current,
        send_notification: sendNotification ?? false,
        file_path: file?.path,
        file_name: file?.name,
        file_type: file?.type ?? undefined,
        file_size: file?.size ?? undefined,
      });
      return { error };
    },
    [channel, isConnected]
  );

  const deleteMessage = useCallback(async (message: ChatMessage) => {
    const { error } = await supabase.from('chat_rooms_messages').delete().eq('id', message.id);
    if (error) {
      console.error(error);
      return;
    }

    setMessages((current) => current.filter((m) => m.id !== message.id));
    setDeletedMessageIds((current) => new Set(current).add(message.id));

    // The row is gone, so nothing references the object any more; drop it too.
    if (message.file) await removeChatFile(message.file.path);
  }, []);

  return { messages, sendMessage, deleteMessage, deletedMessageIds, isConnected };
}
